import React from "react"

import { Textarea } from "@coval/ui"
import { ChevronDown, FileText } from "lucide-react"

import { MAX_TRANSCRIPT_SIZE_BYTES } from "./types"

interface TranscriptEditorProps {
  transcript: string | undefined
  isExpanded: boolean
  onToggle: () => void
  onTranscriptChange: (text: string) => void
  onError: (message: string | null) => void
}

const TranscriptEditor: React.FC<TranscriptEditorProps> = ({
  transcript,
  isExpanded,
  onToggle,
  onTranscriptChange,
  onError,
}) => (
  <div className="px-2 pb-2">
    <button
      type="button"
      className="flex items-center gap-1 text-xs text-coval-text-muted hover:text-coval-text transition-colors"
      aria-expanded={isExpanded}
      aria-label={
        transcript
          ? "Edit ground truth transcript"
          : "Add ground truth transcript"
      }
      onClick={onToggle}
    >
      <ChevronDown
        className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-180" : ""}`}
      />
      {transcript
        ? "Edit ground truth transcript"
        : "Add ground truth transcript"}
    </button>

    {isExpanded && (
      <div className="mt-2 space-y-2">
        <Textarea
          aria-label="Ground truth transcript"
          placeholder="Paste the expected transcript for this audio file..."
          value={transcript || ""}
          onChange={(e) => {
            onTranscriptChange(e.target.value)
            onError(null)
          }}
          className="min-h-[56px]"
        />
        <div className="flex items-center gap-2">
          <span className="text-xs text-coval-text-muted">or</span>
          <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-coval-contrast hover:text-coval-contrast/80 transition-colors">
            <FileText className="w-3 h-3" />
            Upload transcript file
            <input
              type="file"
              accept=".txt,.json"
              className="hidden"
              aria-label="Upload transcript file"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (!file) {
                  return
                }
                if (file.size > MAX_TRANSCRIPT_SIZE_BYTES) {
                  onError(
                    `Transcript file too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max size: ${MAX_TRANSCRIPT_SIZE_BYTES / 1024 / 1024} MB`,
                  )
                  e.target.value = ""
                  return
                }
                const reader = new FileReader()
                reader.onerror = () => {
                  onError("Failed to read transcript file")
                }
                reader.onload = (event) => {
                  const text = event.target?.result
                  if (typeof text !== "string") {
                    return
                  }
                  onTranscriptChange(text)
                  onError(null)
                }
                reader.readAsText(file)
                e.target.value = ""
              }}
            />
          </label>
        </div>
      </div>
    )}
  </div>
)

export default TranscriptEditor
